import type { GeoPoint, OrderInfo, AddressSuggestion } from '@@partner:types:app'

import React, { useCallback, useState, useEffect } from 'react'
import { Button, Form, Panel, Input } from 'rsuite'
import { useEditOrderInfo } from '@@partner:graphql/mutation/order/editOrderInfo.gql'
import { notifyOk, notifyError } from '@@partner:helpers/notification'
import AddressAutosuggest from '../form/AddressAutoSuggest'
import OrderMapMarker, { DEFAULT_MAP_CENTER } from './OrderMapMarker'

import './EditAddressAndCoordinates.scss'

export interface EditAddressAndCoordinatesProps {
  order: OrderInfo
  onCancel: () => void
  onSave: () => void
}

export default function EditAddressAndCoordinates ({
  order,
  onCancel,
  onSave
}: EditAddressAndCoordinatesProps) {
  const [ address, setAddress ] = useState(order.address || '')
  const [ addressAppartments, setAddressAppartments ] = useState(order.address_appartments || '')
  const [ addressIntercom, setAddressIntercom ] = useState(order.address_intercom || '')
  const [ addressPorch, setAddressPorch ] = useState(order.address_porch || '')
  const [ addressFloor, setAddressFloor ] = useState(order.address_floor || '')
  
  const orderHasValidCoords = order.customerGeoPoint?.latitude && 
    order.customerGeoPoint?.longitude &&
    order.customerGeoPoint.latitude !== 0 &&
    order.customerGeoPoint.longitude !== 0

  const [ geoPoint, setGeoPoint ] = useState<GeoPoint>(
    orderHasValidCoords
      ? {
        latitude: order.customerGeoPoint.latitude,
        longitude: order.customerGeoPoint.longitude
      }
      : DEFAULT_MAP_CENTER
  )

  const [ editOrderInfo, { loading } ] = useEditOrderInfo()

  useEffect(() => {
    if (order.address) {
      setAddress(order.address)
    }
    setAddressAppartments(order.address_appartments || '')
    setAddressIntercom(order.address_intercom || '')
    setAddressPorch(order.address_porch || '')
    setAddressFloor(order.address_floor || '')
    if (order.customerGeoPoint && order.customerGeoPoint.latitude && order.customerGeoPoint.longitude) {
      setGeoPoint({
        latitude: order.customerGeoPoint.latitude,
        longitude: order.customerGeoPoint.longitude
      })
    }
    else {
      setGeoPoint(DEFAULT_MAP_CENTER)
    }
  }, [ order ])

  const handleSuggestionSelected = useCallback(
    (_event: any, data: any) => {
      const suggestion = data.suggestion || data
      if (suggestion) {
        const newAddress = suggestion.value || suggestion.suggestionValue || ''
        setAddress(newAddress)
        
        // Координаты в формате DaData: data.geo_lat и data.geo_lon (строки)
        const lat = suggestion.data?.geo_lat
        const lon = suggestion.data?.geo_lon
        
        if (lat && lon) {
          setGeoPoint({
            latitude: parseFloat(lat),
            longitude: parseFloat(lon)
          })
        }
      }
    },
    []
  )

  const handleAddressChange = useCallback((value?: string) => {
    if (value !== undefined) {
      setAddress(value)
    }
  }, [])

  const handleGeoPointChange = useCallback((newGeoPoint: GeoPoint) => {
    setGeoPoint(newGeoPoint)
  }, [])

  const handleSave = async () => {
    try {
      const result = await editOrderInfo({
        variables: {
          order_id: order.id,
          newData: {
            address,
            address_appartments: addressAppartments || null,
            address_intercom: addressIntercom || null,
            address_porch: addressPorch || null,
            address_floor: addressFloor || null,
            customerGeoPoint: {
              latitude: geoPoint.latitude,
              longitude: geoPoint.longitude
            }
          }
        }
      })

      if (result.data?.updated) {
        notifyOk('Адрес и координаты успешно обновлены')
        onSave()
      }
    }
    catch (error) {
      notifyError('Не удалось обновить адрес и координаты')
      console.error(error)
    }
  }

  return (
    <div className="EditAddressAndCoordinates">
      <Form>
        <Form.Group>
          <Form.ControlLabel>Адрес доставки</Form.ControlLabel>
          <AddressAutosuggest
            value={address}
            onChange={handleAddressChange}
            onSuggestionSelected={handleSuggestionSelected}
            placeholder="Введите адрес доставки"
          />
        </Form.Group>

        <div className="EditAddressAndCoordinates__AddressDetails">
          <Form.Group>
            <Form.ControlLabel>Квартира</Form.ControlLabel>
            <Input
              value={addressAppartments}
              onChange={setAddressAppartments}
              placeholder="Номер квартиры"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Подъезд</Form.ControlLabel>
            <Input
              value={addressPorch}
              onChange={setAddressPorch}
              placeholder="Номер подъезда"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Этаж</Form.ControlLabel>
            <Input
              value={addressFloor}
              onChange={setAddressFloor}
              placeholder="Этаж"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Домофон</Form.ControlLabel>
            <Input
              value={addressIntercom}
              onChange={setAddressIntercom}
              placeholder="Код домофона"
            />
          </Form.Group>
        </div>

        <Panel header="Местоположение на карте" bordered className="mt-3">
          <OrderMapMarker
            geoPoint={geoPoint}
            onPositionChanged={handleGeoPointChange}
          />
        </Panel>

        <div className="EditAddressAndCoordinates__Actions">
          <Button
            appearance="primary"
            onClick={handleSave}
            loading={loading}
            disabled={!address || !geoPoint.latitude || !geoPoint.longitude}
          >
            Сохранить
          </Button>
          <Button appearance="subtle" onClick={onCancel}>
            Отмена
          </Button>
        </div>
      </Form>
    </div>
  )
}
